import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { LayoutGrid, Loader2, ChevronDown, ImagePlus } from "lucide-react";
import api from "@/lib/api";
import { useRoofLayout } from "./layout/useRoofLayout";
import { LayoutStage } from "./layout/LayoutStage";
import { LayoutModeBar } from "./layout/LayoutModeBar";
import { LayoutArrayControls } from "./layout/LayoutArrayControls";
import { LayoutSourceBar } from "./layout/LayoutSourceBar";
import { LayoutStatus } from "./layout/LayoutStatus";
import { LayoutAdjustDialog, LayoutPreviewDialog } from "./layout/LayoutDialogs";
import {
  uid, parseModuleDims, baseModule, centroid, rotateAround, scaleAround, gridPlace, buildRefs, fmtKwp, fmtEur,
} from "./layout/layoutMath";

// Hält Bezug (Angebot/Konfiguration) stabil, auch während die Liste nach einer Anpassung neu lädt.
function useRefSelection(refs, layout) {
  const [refKey, setRefKey] = useState("");
  const pending = useRef(null);
  useEffect(() => {
    if (refs.some((r) => r.key === refKey)) { if (pending.current === refKey) pending.current = null; return; }
    if (pending.current) return;
    const fromLayout = layout?.ref_id ? `${layout.ref_kind}:${layout.ref_id}` : null;
    setRefKey(refs.find((r) => r.key === fromLayout)?.key || refs[0]?.key || "");
  }, [refs, layout, refKey]);
  const expect = (key) => { pending.current = key; setRefKey(key); };
  return [refKey, setRefKey, expect];
}

export default function RoofLayoutEditor({ projectId, catalog, savedConfigs, pvOffers, onAdjusted }) {
  const [open, setOpen] = useState(false);
  const { meta, setMeta, source, setSource, img, imgError } = useRoofLayout(projectId, open);
  const refs = useMemo(() => buildRefs(savedConfigs, pvOffers, catalog), [savedConfigs, pvOffers, catalog]);
  const [refKey, setRefKey, expectRef] = useRefSelection(refs, meta?.layout);
  const ref = refs.find((r) => r.key === refKey) || null;
  const planned = ref?.modules || 0;
  const [panels, setPanelsRaw] = useState([]);
  const [mod, setMod] = useState(null);
  const [groupAngle, setGroupAngle] = useState(0);
  const [mode, setMode] = useState("move");
  const [selected, setSelected] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dialog, setDialog] = useState(null);
  const reset = useRef({ count: null, force: false });
  const setPanels = (fn) => { setPanelsRaw(fn); setDirty(true); };
  const center = img ? { x: img.w / 2, y: img.h / 2 } : { x: 0, y: 0 };

  useEffect(() => {
    if (!open || !meta || source) return;
    const s = meta.layout?.image_source;
    setSource(s && meta.sources[s] ? s : meta.default_source);
  }, [open, meta, source, setSource]);

  useEffect(() => {
    if (!img || !meta) return;
    const base = baseModule(img, meta.sources[img.source]?.m_per_px, parseModuleDims(ref?.comp?.specs?.size));
    const lay = meta.layout;
    const keep = !reset.current.force && lay?.image_source === img.source && lay.image_w === img.w && (lay.panels || []).length > 0;
    if (keep) {
      setPanelsRaw(lay.panels.map((p) => ({ ...p, id: uid() })));
      setMod({ long: lay.module_long_px, short: lay.module_short_px, base: base.long });
      setGroupAngle(lay.group_angle || 0);
      setDirty(false);
    } else {
      const n = reset.current.count ?? planned;
      setMod({ ...base, base: base.long });
      setGroupAngle(0);
      setPanelsRaw(n > 0 ? gridPlace(n, base, { x: img.w / 2, y: img.h / 2 }) : []);
      setDirty(n > 0);
    }
    reset.current = { count: null, force: false };
    setSelected(null);
    setZoom((img.source === "google_solar" ? 2.5 : 1) * (window.innerWidth < 640 ? 2 : 1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [img]);

  const selectedPanel = panels.find((p) => p.id === selected) || null;
  const majorityLandscape = panels.filter((p) => p.landscape).length > panels.length / 2;
  const addAt = (pt) => { const id = uid(); setPanels((ps) => [...ps, { id, x: pt.x, y: pt.y, angle: groupAngle, landscape: false }]); setSelected(id); };
  const rotateSel = () => setPanels((ps) => ps.map((p) => (p.id === selected ? { ...p, landscape: !p.landscape } : p)));
  const deleteSel = () => { setPanels((ps) => ps.filter((p) => p.id !== selected)); setSelected(null); };
  const regrid = (n, landscape) => { setPanels((ps) => gridPlace(n, mod, centroid(ps, center), groupAngle, landscape)); setSelected(null); };
  const setAngle = (a) => {
    const delta = a - groupAngle;
    if (!delta) return;
    setPanels((ps) => rotateAround(ps, centroid(ps, center), delta));
    setGroupAngle(a);
  };
  const setSize = (pct) => {
    const long = (mod.base * pct) / 100;
    const f = long / mod.long;
    setPanels((ps) => scaleAround(ps, centroid(ps, center), f));
    setMod({ ...mod, long, short: mod.short * f });
  };
  const resetFor = () => { reset.current = { count: panels.length || planned, force: true }; };
  const changeSource = (s) => { if (s !== source) { resetFor(); setSource(s); } };
  const onUploaded = (data) => { resetFor(); setMeta(data); setSource("upload"); };
  const onRemoved = (data) => { resetFor(); setMeta(data); setSource(data.default_source); };
  const onKey = (e) => {
    if (!selected) return;
    if (e.key === "Delete" || e.key === "Backspace") { e.preventDefault(); deleteSel(); }
    if (e.key === "r" || e.key === "R") rotateSel();
  };

  const doSave = async (adjust) => {
    setSaving(true);
    try {
      const { data } = await api.put(`/projects/${projectId}/pv-layout`, {
        image_source: img.source, image_w: img.w, image_h: img.h, module_long_px: mod.long, module_short_px: mod.short,
        group_angle: groupAngle, panels: panels.map(({ id, ...p }) => p),
        ref_kind: ref?.kind || null, ref_id: ref?.id || null, adjust,
      });
      setMeta((m) => ({ ...m, layout: data.layout }));
      setDirty(false);
      setDialog(null);
      const a = data.adjusted;
      if (a?.kind === "offer") toast.success(`Neues Angebot ${a.offer_number}: ${a.modules_after} Module · ${fmtKwp(a.total_kwp)} kWp · ${fmtEur(a.total_gross)} – ${a.previous_number} wurde abgelöst`);
      else if (a?.kind === "config") toast.success(`„${a.name}“ angepasst: ${a.modules_after} Module · ${fmtKwp(a.total_kwp)} kWp · ${fmtEur(a.total_gross)}`);
      else toast.success(`Modulbelegung gespeichert (${panels.length} Module)`);
      if (a) { expectRef(`${a.kind}:${a.new_id || a.id}`); onAdjusted?.(); }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setSaving(false); }
  };
  const save = () => (ref && panels.length > 0 && panels.length !== planned ? setDialog("adjust") : doSave(false));

  const savedCount = meta?.layout?.panels?.length || 0;
  return (
    <section className="bg-[#18181B] border border-zinc-800 rounded-2xl p-4 sm:p-5 space-y-4" data-testid="roof-layout-section">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h2 className="font-display text-lg font-bold flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="inline-flex items-center gap-2"><LayoutGrid size={18} className="text-[#0EA5E9]" /> Modulbelegung Dachbild</span>
            {savedCount > 0 && <span className="px-2 py-0.5 rounded-md bg-[#0EA5E9]/15 text-[#0EA5E9] text-[11px] font-bold whitespace-nowrap" data-testid="roof-layout-saved-badge">{savedCount} Module geplant</span>}
          </h2>
          <p className="text-[12px] text-zinc-400 mt-0.5">Module aufs Dach legen, verschieben, drehen oder löschen – der Plan erscheint im PV-Angebot.</p>
        </div>
        <button type="button" onClick={() => setOpen((o) => !o)} data-testid="roof-layout-toggle"
          className="h-10 px-4 rounded-lg bg-[#0EA5E9]/15 border border-[#0EA5E9]/50 text-sky-100 font-bold text-[13px] inline-flex items-center gap-1.5 hover:bg-[#0EA5E9]/25">
          {open ? "Schließen" : savedCount ? "Belegung bearbeiten" : "Belegung planen"}
          <ChevronDown size={15} className={`transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      </div>

      {open && !meta && <div className="h-40 flex items-center justify-center"><Loader2 className="animate-spin text-zinc-400" /></div>}
      {open && meta && (
        <div className="space-y-3 animate-in fade-in duration-300">
          {source && <LayoutSourceBar projectId={projectId} sources={meta.sources} source={source} onSource={changeSource} onUploaded={onUploaded} onRemoved={onRemoved} />}
          {!source && (
            <div className="rounded-xl border border-dashed border-zinc-700 bg-[#0F0F11] px-4 py-10 text-center space-y-3" data-testid="roof-layout-empty">
              <ImagePlus size={30} className="mx-auto text-zinc-500" />
              <p className="text-[13px] text-zinc-300">Für dieses Projekt gibt es noch kein Luftbild vom Dach.<br />Lade ein Foto hoch – z. B. eine Drohnen- oder Satellitenaufnahme.</p>
              <LayoutSourceBar projectId={projectId} sources={meta.sources} source={source} onSource={changeSource} onUploaded={onUploaded} onRemoved={onRemoved} big />
            </div>
          )}
          {source && imgError && <p className="text-[13px] text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2" data-testid="roof-layout-image-error">{imgError}</p>}
          {source && !imgError && (!img || !mod) && <div className="h-56 flex items-center justify-center rounded-xl bg-black/40"><Loader2 className="animate-spin text-zinc-400" /></div>}
          {img && mod && (
            <>
              <LayoutModeBar mode={mode} setMode={setMode} selectedPanel={selectedPanel} onRotate={rotateSel} onDelete={deleteSel} />
              <LayoutStage img={img} panels={panels} mod={mod} selected={selected} mode={mode} zoom={zoom}
                onSelect={setSelected} setPanels={setPanels} onAdd={addAt} onKey={onKey} />
              <LayoutArrayControls angle={groupAngle} onAngle={setAngle} landscape={majorityLandscape}
                onOrient={(l) => regrid(panels.length, l)} onRearrange={() => regrid(panels.length, majorityLandscape)}
                sizePct={(mod.long / mod.base) * 100} onSize={setSize} zoom={zoom} setZoom={setZoom} disabled={!panels.length} />
              <LayoutStatus refs={refs} refKey={refKey} setRefKey={setRefKey} count={panels.length} planned={planned}
                dirty={dirty} saving={saving} canPreview={savedCount > 0} onApplyPlanned={() => regrid(planned, majorityLandscape)}
                onPreview={() => setDialog("preview")} onSave={save} />
            </>
          )}
        </div>
      )}
      <LayoutAdjustDialog open={dialog === "adjust"} onClose={() => setDialog(null)} refInfo={ref} count={panels.length} saving={saving} onSave={doSave} />
      <LayoutPreviewDialog open={dialog === "preview"} onClose={() => setDialog(null)} projectId={projectId} />
    </section>
  );
}
